import {
  DEFAULT_CONTRACTED_TASK_GROUPS,
  DEFAULT_PRECON_TASK_GROUPS,
  projectTaskGroupSectionRank,
} from './taskGroupOrder';

export type GroupTimelineColorEntryKind = 'group' | 'task';

export interface GroupTimelineColorEntry {
  id: string;
  kind: GroupTimelineColorEntryKind;
  /** Normalized match key, e.g. "production", "timber lead time". */
  matchKey: string;
  label: string;
  color: string;
}

export interface GroupTimelineColorsDoc {
  entries: GroupTimelineColorEntry[];
  updatedAt: Date | null;
  updatedByUid: string | null;
  updatedByName: string | null;
}

const HEX_COLOR = /^#[0-9A-Fa-f]{6}$/;

export function normalizeTimelineColorKey(name: string): string {
  return (name || '').trim().toLowerCase().replace(/\s+/g, ' ');
}

function stableId(prefix: string, matchKey: string): string {
  return `${prefix}-${matchKey.replace(/[^a-z0-9]+/g, '-')}`;
}

/** Default palette — seeded into Settings and used when Firestore has no doc yet. */
export function seedGroupTimelineColorEntries(): GroupTimelineColorEntry[] {
  const out: GroupTimelineColorEntry[] = [];
  const seen = new Set<string>();

  const push = (kind: GroupTimelineColorEntryKind, label: string, color: string) => {
    const matchKey = normalizeTimelineColorKey(label);
    if (!matchKey || seen.has(`${kind}:${matchKey}`)) return;
    seen.add(`${kind}:${matchKey}`);
    out.push({
      id: stableId(kind, matchKey),
      kind,
      matchKey,
      label: label.trim(),
      color,
    });
  };

  for (const g of DEFAULT_PRECON_TASK_GROUPS) push('group', g.name, g.color);
  for (const g of DEFAULT_CONTRACTED_TASK_GROUPS) push('group', g.name, g.color);

  push('task', 'Timber Lead Time', '#2563EB');
  push('task', 'Timber Package Lead', '#2563EB');
  push('task', 'Cut Timber Package', '#D97706');

  return out;
}

export function parseGroupTimelineColorEntry(raw: unknown): GroupTimelineColorEntry | null {
  if (!raw || typeof raw !== 'object') return null;
  const row = raw as Record<string, unknown>;
  const kind = row.kind === 'task' ? 'task' : row.kind === 'group' ? 'group' : null;
  const matchKey = normalizeTimelineColorKey(String(row.matchKey ?? row.label ?? ''));
  const label = String(row.label ?? '').trim();
  const color = String(row.color ?? '').trim();
  const id = typeof row.id === 'string' && row.id.trim() ? row.id.trim() : stableId(kind ?? 'group', matchKey);
  if (!kind || !matchKey || !label || !HEX_COLOR.test(color)) return null;
  return { id, kind, matchKey, label, color };
}

export function parseGroupTimelineColorsDoc(data: Record<string, unknown> | undefined | null): GroupTimelineColorsDoc {
  const entriesRaw = data?.entries;
  const entries: GroupTimelineColorEntry[] = [];
  if (Array.isArray(entriesRaw)) {
    for (const item of entriesRaw) {
      const parsed = parseGroupTimelineColorEntry(item);
      if (parsed) entries.push(parsed);
    }
  }
  const toDate = (v: unknown): Date | null => {
    const d = (v as { toDate?: () => Date } | undefined)?.toDate?.();
    return d instanceof Date && !Number.isNaN(d.getTime()) ? d : null;
  };
  return {
    entries: entries.length ? entries : seedGroupTimelineColorEntries(),
    updatedAt: toDate(data?.updatedAt),
    updatedByUid: typeof data?.updatedByUid === 'string' ? data.updatedByUid : null,
    updatedByName: typeof data?.updatedByName === 'string' ? data.updatedByName : null,
  };
}

function groupNameMatchesEntry(groupName: string, entry: GroupTimelineColorEntry): boolean {
  if (entry.kind !== 'group') return false;
  const g = normalizeTimelineColorKey(groupName);
  if (!g) return false;
  if (g === entry.matchKey) return true;
  if (g.includes(entry.matchKey) || entry.matchKey.includes(g)) return true;
  const rank = projectTaskGroupSectionRank(groupName);
  if (rank >= 900) return false;
  const labelRank = projectTaskGroupSectionRank(entry.label);
  return rank === labelRank;
}

function taskTitleMatchesEntry(taskTitle: string, entry: GroupTimelineColorEntry): boolean {
  if (entry.kind !== 'task') return false;
  const t = normalizeTimelineColorKey(taskTitle);
  if (!t) return false;
  return t === entry.matchKey || t.includes(entry.matchKey);
}

/**
 * Admin palette for All-projects timeline (color mode Group).
 * Task titles win over group names; unknown names fall back to Firestore group.color.
 */
export function resolveCanonicalTimelineColor(opts: {
  taskTitle?: string;
  groupName?: string;
  entries: GroupTimelineColorEntry[];
}): string | null {
  const entries = opts.entries?.length ? opts.entries : seedGroupTimelineColorEntries();
  const title = (opts.taskTitle || '').trim();
  const groupName = (opts.groupName || '').trim();

  if (title) {
    for (const e of entries) {
      if (taskTitleMatchesEntry(title, e)) return e.color;
    }
  }
  if (groupName) {
    for (const e of entries) {
      if (groupNameMatchesEntry(groupName, e)) return e.color;
    }
  }
  return null;
}

export function newGroupTimelineColorEntry(
  kind: GroupTimelineColorEntryKind,
  label: string,
  color: string,
): GroupTimelineColorEntry {
  const matchKey = normalizeTimelineColorKey(label);
  return {
    id: typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : stableId(kind, `${matchKey}-${Date.now()}`),
    kind,
    matchKey,
    label: label.trim() || 'Untitled',
    color: HEX_COLOR.test(color) ? color : '#6B7280',
  };
}
